import type {
  ScheduleUpdatedEmailPayload,
  ScheduleUpdatedEmailResponse,
} from "types/updatePaymentPlan";
import { getUpdatePlanFailure } from "utils/updatePaymentPlanUtils";

export const SCHEDULE_UPDATED_EMAIL_FAILURE_WARNING =
  "Payment plan updated, but the notification email could not be sent.";
export const SCHEDULE_UPDATED_EMAIL_PARTIAL_WARNING =
  "Payment plan updated, but the notification email was only partially delivered.";

type PaymentPlanUpdateResponse = {
  body?: {
    success?: boolean;
    data?: {
      schedule_id?: number | string;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  [key: string]: unknown;
};

interface SendScheduleUpdatedEmailAfterUpdateOptions<
  TUpdateResponse extends PaymentPlanUpdateResponse,
> {
  updateResponse: TUpdateResponse;
  accountsId: unknown;
  clusterId: unknown;
  fallbackScheduleId?: unknown;
  notificationOutcomes: Map<string, string | null>;
  sendEmail: (
    payload: ScheduleUpdatedEmailPayload
  ) => Promise<ScheduleUpdatedEmailResponse>;
}

export interface ScheduleUpdatedEmailOutcome<
  TUpdateResponse extends PaymentPlanUpdateResponse,
> {
  updateResponse: TUpdateResponse;
  emailRequested: boolean;
  warning: string | null;
}

const toNumericDatabaseId = (value: unknown): number | null => {
  const id = Number(value);

  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

/**
 * Sends the schedule-update notification only after the existing plan-update
 * success checks pass. Email delivery is deliberately converted to a warning
 * instead of an exception so it can never replace the successful plan result.
 */
export const sendScheduleUpdatedEmailAfterUpdate = async <
  TUpdateResponse extends PaymentPlanUpdateResponse,
>({
  updateResponse,
  accountsId,
  clusterId,
  fallbackScheduleId,
  notificationOutcomes,
  sendEmail,
}: SendScheduleUpdatedEmailAfterUpdateOptions<TUpdateResponse>): Promise<
  ScheduleUpdatedEmailOutcome<TUpdateResponse>
> => {
  if (getUpdatePlanFailure(updateResponse?.body)) {
    return { updateResponse, emailRequested: false, warning: null };
  }

  const accounts_id = toNumericDatabaseId(accountsId);
  const cluster_id = toNumericDatabaseId(clusterId);
  const schedule_id = toNumericDatabaseId(
    updateResponse?.body?.data?.schedule_id ?? fallbackScheduleId
  );

  if (accounts_id === null || cluster_id === null || schedule_id === null) {
    return {
      updateResponse,
      emailRequested: false,
      warning: SCHEDULE_UPDATED_EMAIL_FAILURE_WARNING,
    };
  }

  const payload: ScheduleUpdatedEmailPayload = {
    accounts_id,
    cluster_id,
    schedule_id,
  };
  const notificationKey = `${accounts_id}:${cluster_id}:${schedule_id}`;

  if (notificationOutcomes.has(notificationKey)) {
    return {
      updateResponse,
      emailRequested: false,
      warning: notificationOutcomes.get(notificationKey) ?? null,
    };
  }

  // Reserve the key before awaiting so concurrent callbacks cannot send twice.
  notificationOutcomes.set(notificationKey, null);

  try {
    const response = await sendEmail(payload);
    const warning =
      Number(response?.status) === 207
        ? SCHEDULE_UPDATED_EMAIL_PARTIAL_WARNING
        : response?.body?.success === true
          ? null
          : SCHEDULE_UPDATED_EMAIL_FAILURE_WARNING;

    notificationOutcomes.set(notificationKey, warning);

    return { updateResponse, emailRequested: true, warning };
  } catch (_error) {
    notificationOutcomes.set(
      notificationKey,
      SCHEDULE_UPDATED_EMAIL_FAILURE_WARNING
    );

    return {
      updateResponse,
      emailRequested: true,
      warning: SCHEDULE_UPDATED_EMAIL_FAILURE_WARNING,
    };
  }
};
